import { createApi } from '@reduxjs/toolkit/query/react';

import {
  ATC_BEELINE_CHUNK,
  ATC_MANGO_CHUNK,
  DELETE,
  GET,
  POST,
  PUT,
  SOCIAL_INTEGRATIONS,
} from '@app/constants';
import { customBaseQuery } from '@app/services/rtkQuery/baseQuery';
import type {
  NSocialIntegrations,
  SendMessageToClientRequest,
  SendMessageToClientResponse,
} from '@app/services/rtkQuery/SocialIntegrations/types';
import { Methods } from '@app/services/rtkQuery/types';
import type { WithActiveBranchId } from '@app/types';

enum Endpoints {
  GetAtcBeelineAbonents = 'GetAtcBeelineAbonents',
}

// Бэк доставляет сообщение в шлюз синхронно внутри этого запроса (для
// WhatsApp — HTTP-вызов с собственным таймаутом 30 с), поэтому запрос легально
// долгий. Но без верхней границы зависший запрос блокирует поле ввода чата
// навсегда — глобального axios-таймаута в проекте нет.
export const SEND_MESSAGE_TO_CLIENT_TIMEOUT_MS = 60_000;
export const socialIntegrationsApi = createApi({
  reducerPath: 'socialIntegrationsApi',
  tagTypes: ['CheckTokenAndListAbonets', 'Integration1C'],
  baseQuery: ({ url, ...props }) =>
    customBaseQuery({ url: `${SOCIAL_INTEGRATIONS}/${url}`, ...props }),
  endpoints: ({ query, mutation }) => ({
    CheckBeelineTokenAndListAbonets: query<
      NSocialIntegrations.CheckBeelineTokenAndListAbonetsResponse,
      NSocialIntegrations.CheckBeelineTokenAndListAbonetsRequest
    >({
      query: ({ ...params }) => ({
        url: `${ATC_BEELINE_CHUNK}/CheckTokenAndListAbonets`,
        method: GET,
        params,
      }),
      providesTags: ['CheckTokenAndListAbonets'],
    }),
    CheckMangoTokenAndListAbonets: query<
      NSocialIntegrations.CheckMangoTokenAndListAbonetsResponse,
      NSocialIntegrations.CheckMangoTokenAndListAbonetsRequest
    >({
      query: ({ ...params }) => ({
        url: `${ATC_MANGO_CHUNK}/CheckTokenAndListAbonets`,
        method: GET,
        params,
      }),
      providesTags: ['CheckTokenAndListAbonets'],
    }),
    AddAtcBeelineIntegration: mutation<
      NSocialIntegrations.AddAtcBeelineIntegrationResponse,
      NSocialIntegrations.AddAtcBeelineIntegrationRequest
    >({
      query: ({ ActiveBranchId, ...data }) => ({
        url: `${ATC_BEELINE_CHUNK}/AddIntegration`,
        method: POST,
        data,
        params: { ActiveBranchId },
      }),
      invalidatesTags: ['CheckTokenAndListAbonets'],
    }),
    UpdateAtcBeelineIntegration: mutation<
      NSocialIntegrations.UpdateAtcBeelineIntegrationResponse,
      NSocialIntegrations.UpdateAtcBeelineIntegrationRequest
    >({
      query: ({ ActiveBranchId, ...data }) => ({
        url: `${ATC_BEELINE_CHUNK}/UpdateIntegration`,
        method: PUT,
        params: { ActiveBranchId },
        data,
      }),
      invalidatesTags: ['CheckTokenAndListAbonets'],
    }),
    UpdateAtcMangoIntegration: mutation<
      NSocialIntegrations.UpdateAtcMangoIntegrationResponse,
      NSocialIntegrations.UpdateAtcMangoIntegrationRequest
    >({
      query: ({ ActiveBranchId, ...data }) => ({
        url: `${ATC_MANGO_CHUNK}/UpdateIntegration`,
        method: PUT,
        params: { ActiveBranchId },
        data,
      }),
      invalidatesTags: ['CheckTokenAndListAbonets'],
    }),
    AddAtcMangoIntegration: mutation<
      NSocialIntegrations.AddAtcMangoIntegrationResponse,
      NSocialIntegrations.AddAtcMangoIntegrationRequest
    >({
      query: ({ ActiveBranchId, ...data }) => ({
        url: `${ATC_MANGO_CHUNK}/AddIntegration`,
        method: POST,
        data,
        params: { ActiveBranchId },
      }),
      invalidatesTags: ['CheckTokenAndListAbonets'],
    }),
    EditSocialIntegration: mutation<
      NSocialIntegrations.EditSocialIntegrationResponse,
      NSocialIntegrations.EditSocialIntegrationRequest
    >({
      query: ({ ActiveBranchId, ...data }) => ({
        url: 'EditSocialIntegration',
        method: POST,
        data,
        params: { ActiveBranchId },
      }),
    }),
    SendMessageToClient: mutation<
      SendMessageToClientResponse,
      SendMessageToClientRequest
    >({
      query: ({ ActiveBranchId, data, logs }) => ({
        url: 'SendMessageToClient',
        method: POST,
        params: { ActiveBranchId },
        data,
        logs,
        timeout: SEND_MESSAGE_TO_CLIENT_TIMEOUT_MS,
      }),
    }),
    GetActiveChannels: query<
      NSocialIntegrations.GetActiveChannelsResponse,
      NSocialIntegrations.GetActiveChannelsRequest
    >({
      query: (params) => ({
        url: 'GetActiveChannels',
        method: GET,
        params,
      }),
    }),
    GetActiveChannelsWithSender: query<
      NSocialIntegrations.GetActiveChannelsWithSenderResponse,
      NSocialIntegrations.GetActiveChannelsWithSenderRequest
    >({
      query: (params) => ({
        url: 'GetActiveChannelsWithSender',
        method: GET,
        params,
      }),
    }),
    GetSocialIntegrationOperators: query<
      Array<NSocialIntegrations.Operator>,
      NSocialIntegrations.GetSocialIntegrationOperatorsRequest
    >({
      query: (params) => ({
        url: 'GetSocialIntegrationOperators',
        method: GET,
        params,
      }),
    }),
    SetSocialIntegrationOperators: mutation<
      void,
      NSocialIntegrations.SetSocialIntegrationOperatorsRequest
    >({
      query: ({ ActiveBranchId, ...data }) => ({
        url: 'SetSocialIntegrationOperators',
        method: POST,
        params: { ActiveBranchId },
        data,
      }),
    }),
    GetSocialIntegrationsStatus: query<
      NSocialIntegrations.ModelSocialIntegrationsStatus,
      WithActiveBranchId
    >({
      query: (params) => ({
        url: 'GetSocialIntegrationsStatus',
        method: GET,
        params,
      }),
    }),
    GetYookassaGetCodeUrl: query<{ Url: string }, WithActiveBranchId>({
      query: (params) => ({
        url: 'GetYookassaGetCodeUrl',
        method: GET,
        params,
      }),
    }),
    CreateOrUpdateYookassaIntegration: mutation<
      void,
      NSocialIntegrations.AddYookassaDto
    >({
      query: ({ ActiveBranchId, ...data }) => ({
        url: 'CreateOrUpdateYookassaIntegration',
        method: POST,
        params: { ActiveBranchId },
        data,
      }),
    }),

    GetPaymentMethods: query<
      NSocialIntegrations.ModelPaymentMethod[],
      NSocialIntegrations.GetPaymentMethodsRequest
    >({
      query: (params) => ({
        url: 'GetPaymentMethods',
        method: GET,
        params,
      }),
    }),
    GetYookassaIntegration: query<
      NSocialIntegrations.YookassaIntegrationDto,
      NSocialIntegrations.GetYookassaIntegrationRequest
    >({
      query: (params) => ({
        url: 'GetYookassaIntegration',
        method: GET,
        params,
      }),
    }),
    DeleteYookassaIntegration: mutation<
      void,
      NSocialIntegrations.DeleteYookassaIntegrationRequest
    >({
      query: (params) => ({
        url: 'DeleteYookassaIntegration',
        method: DELETE,
        params,
      }),
    }),
    TestIntegration1C: mutation<
      NSocialIntegrations.TestIntegration1CResponse,
      NSocialIntegrations.TestIntegration1CRequest
    >({
      query: ({ ActiveBranchId, ...data }) => ({
        url: 'TestIntegration1C',
        method: POST,
        params: { ActiveBranchId },
        data,
      }),
    }),

    AddIntegration1C: mutation<
      NSocialIntegrations.AddIntegration1CResponse,
      NSocialIntegrations.AddIntegration1CRequest
    >({
      query: ({ ActiveBranchId, ...data }) => ({
        url: 'AddIntegration1C',
        method: POST,
        params: { ActiveBranchId },
        data,
      }),
      invalidatesTags: ['Integration1C'],
    }),
    GetIntegration1CById: query<
      NSocialIntegrations.GetIntegration1CByIdResponse,
      NSocialIntegrations.GetIntegration1CByIdRequest
    >({
      query: (params) => ({
        url: 'GetIntegration1CById',
        method: GET,
        params,
      }),
      providesTags: ['Integration1C'],
    }),

    UpdateIntegration1C: mutation<
      NSocialIntegrations.UpdateIntegration1CResponse,
      NSocialIntegrations.UpdateIntegration1CRequest
    >({
      query: ({ ActiveBranchId, ...data }) => ({
        url: 'UpdateIntegration1C',
        method: PUT,
        params: { ActiveBranchId },
        data,
      }),
      invalidatesTags: ['Integration1C'],
    }),

    UpdateIntegration1COptions: mutation<
      NSocialIntegrations.UpdateIntegration1COptionsResponse,
      NSocialIntegrations.UpdateIntegration1COptionsRequest
    >({
      query: ({ ActiveBranchId, ...data }) => ({
        url: 'UpdateIntegration1COptions',
        method: PUT,
        params: { ActiveBranchId },
        data,
      }),
      invalidatesTags: ['Integration1C'],
    }),
    DeleteIntegration1C: mutation<
      void,
      WithActiveBranchId<{ IntegrationId: number }>
    >({
      query: ({ ActiveBranchId, ...data }) => ({
        url: 'DeleteIntegration1C',
        method: DELETE,
        params: { ActiveBranchId },
        data,
      }),
      invalidatesTags: ['Integration1C'],
    }),
    [Endpoints.GetAtcBeelineAbonents]: query<
      NSocialIntegrations.ModelAtcAbonent[],
      NSocialIntegrations.GetAtcBeelineAbonentsRequest
    >({
      query: (params) => ({
        url: `${ATC_BEELINE_CHUNK}/${Endpoints.GetAtcBeelineAbonents}`,
        method: Methods.GET,
        params,
      }),
    }),
  }),
});

export const {
  useCheckBeelineTokenAndListAbonetsQuery,
  useCheckMangoTokenAndListAbonetsQuery,
  useEditSocialIntegrationMutation,
  useSendMessageToClientMutation,
  useAddAtcBeelineIntegrationMutation,
  useAddAtcMangoIntegrationMutation,
  useUpdateAtcBeelineIntegrationMutation,
  useUpdateAtcMangoIntegrationMutation,
  useLazyGetActiveChannelsQuery,
  useLazyGetSocialIntegrationOperatorsQuery,
  useSetSocialIntegrationOperatorsMutation,
  useGetSocialIntegrationOperatorsQuery,
  useLazyGetActiveChannelsWithSenderQuery,
  useGetSocialIntegrationsStatusQuery,
  useLazyGetSocialIntegrationsStatusQuery,
  useLazyGetYookassaGetCodeUrlQuery,
  useCreateOrUpdateYookassaIntegrationMutation,
  useGetPaymentMethodsQuery,
  useLazyGetYookassaIntegrationQuery,
  useDeleteYookassaIntegrationMutation,
  useGetIntegration1CByIdQuery,
  useLazyGetIntegration1CByIdQuery,
  useUpdateIntegration1CMutation,
  useUpdateIntegration1COptionsMutation,
  useGetActiveChannelsQuery,
  useGetActiveChannelsWithSenderQuery,
  useGetYookassaIntegrationQuery,
  useGetYookassaGetCodeUrlQuery,
  useLazyCheckBeelineTokenAndListAbonetsQuery,
  useLazyCheckMangoTokenAndListAbonetsQuery,
  useLazyGetPaymentMethodsQuery,
  useTestIntegration1CMutation,
  useAddIntegration1CMutation,
  useDeleteIntegration1CMutation,
  useGetAtcBeelineAbonentsQuery,
  useLazyGetAtcBeelineAbonentsQuery,
} = socialIntegrationsApi;
